import * as React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { StoryblokComponent } from '@storyblok/react';
import type { SbBlokData } from '@storyblok/react';

import { defaultLocale, type LocaleAlternate, type LocaleCode } from '../config/locales';
import { Header } from '../components/storyblok/Header';
import { getLocaleFromPath } from '../storyblok/locales/getLocaleFromPath';
import { resolveLang } from '../storyblok/locales/resolveLang';
import type { SiteConfigStoryblok, HeaderStoryblok } from '../types/storyblok';

interface SiteShellProps {
  pathname: string;
  children: React.ReactNode;
  /**
   * Explicit current locale for chrome (header/selector). When provided this
   * wins over the pathname-derived locale; otherwise the path-derived locale
   * is forwarded as a fallback. Storyblok pages pass `pageContext.lang` here
   * via `wrapPageElement`; non-Storyblok pages may omit it.
   */
  currentLocale?: LocaleCode | null;
  /**
   * Hreflang-style alternates for the current page, forwarded to the Header's
   * LanguageSelector. The wrapper (e.g. `wrapPageElement`) is responsible for
   * deriving these; SiteShell does NOT re-derive them. The Header handles the
   * empty-array case by hiding the selector (R13.1).
   */
  alternates?: readonly LocaleAlternate[];
}

interface SiteConfigNode {
  full_slug: string;
  lang: string | null;
  content: string;
}

interface SiteConfigQueryResult {
  allStoryblokEntry: { nodes: SiteConfigNode[] };
}

function pickSiteConfigNode(
  nodes: readonly SiteConfigNode[],
  lang: LocaleCode
): SiteConfigNode | null {
  const exact = nodes.find((n) => resolveLang(n.lang) === lang);
  if (exact) return exact;
  return nodes.find((n) => resolveLang(n.lang) === defaultLocale) ?? null;
}

// Global wrapper that provides Header and Footer from the site-config story.
// Reads ALL locale variants of site-config via useStaticQuery (which runs
// once per build) and selects the variant matching the current page's URL
// at render time. Falls back to the default-locale variant if the requested
// locale's variant is missing.
export const SiteShell: React.FC<SiteShellProps> = ({
  pathname,
  children,
  currentLocale,
  alternates,
}) => {
  const data = useStaticQuery<SiteConfigQueryResult>(graphql`
    query SiteConfigForShell {
      allStoryblokEntry(filter: { slug: { eq: "site-config" } }) {
        nodes {
          full_slug
          lang
          content
        }
      }
    }
  `);

  const lang = getLocaleFromPath(pathname);
  const node = pickSiteConfigNode(data?.allStoryblokEntry.nodes ?? [], lang);

  if (!node) {
    console.warn(
      'SiteShell: site-config story not found; rendering page without global header/footer.'
    );
    return <>{children}</>;
  }

  let siteConfig: (SiteConfigStoryblok & { header?: HeaderStoryblok[] }) | null = null;
  try {
    siteConfig = JSON.parse(node.content) as SiteConfigStoryblok & { header?: HeaderStoryblok[] };
  } catch {
    console.warn(
      'SiteShell: site-config story content is not valid JSON; rendering page without global header/footer.'
    );
    return <>{children}</>;
  }

  const headerBlok = siteConfig?.header?.[0];
  const footerBlok = siteConfig?.footer?.[0];

  // Detect homepage: root path or locale-only paths like /en/, /de/
  const isHomePage = pathname === '/' || /^\/[a-z]{2}\/?$/.test(pathname);

  return (
    <>
      <Header
        currentLocale={currentLocale ?? lang}
        alternates={alternates}
        blok={headerBlok}
        isHomePage={isHomePage}
      />
      {children}
      {footerBlok && <StoryblokComponent blok={footerBlok as unknown as SbBlokData} />}
    </>
  );
};
